'use client'

import { useGetFatalError, useErrorActions } from 'src/store/error'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import type { Hero } from 'src/types/infra/supabase'
import One55SymbolWhite from 'src/app/assets/icons/ico-155-symbol.svg'

type Props = Readonly<{
  hero?: Hero
}>

export function ErrorView({ hero }: Props) {
  const fatalError = useGetFatalError()
  const errorActions = useErrorActions()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  if (!fatalError) {
    return null
  }

  const handleRetry = () => {
    // Clear the fatal error
    errorActions.clearFatalError()

    // Reload the page to restart the application
    window.location.reload()
  }

  const getErrorMessage = () => {
    switch (fatalError.type) {
      case 'PLAYER_CHANNEL_FAILED':
        if (fatalError.reason === 'Failed to fetch player data') {
          return t('Unable to retrieve your player data. Please try again.')
        }
        return t('Unable to connect to the game server. Please try again.')
      case 'AUTHENTICATION_FAILED':
        return t('Authentication failed. Please try again.')
      case 'FATAL_CONNECTION_ERROR':
        return t('A connection error occurred. Please try again.')
      default:
        return t('An unexpected error occurred. Please try again.')
    }
  }

  const getErrorTitle = (): string => {
    switch (fatalError.type) {
      case 'PLAYER_CHANNEL_FAILED':
        return t('Connection Error')
      case 'AUTHENTICATION_FAILED':
        return t('Authentication Error')
      case 'FATAL_CONNECTION_ERROR':
        return t('Connection Error')
      default:
        return t('Error')
    }
  }

  return (
    <div className="race-loading-indicator-outer">
      <div className="race-loading-indicator">
        <div className="race-loading-indicator__inner">
          <One55SymbolWhite />
          <div className="text">
            <span className="primary">{getErrorTitle()}</span>
            <span className="secondary">{getErrorMessage()}</span>

            <button onClick={handleRetry} type="button">
              <span>{t('Try Again')}</span>
            </button>
          </div>
        </div>
      </div>
      {hero ? (
        <div className="loading-indicator__background">
          {hero.image ? (
            <video
              autoPlay
              controlsList="nodownload"
              disablePictureInPicture
              loop
              muted
              playsInline
              poster={`/assets/tracks-backgrounds/${hero.image}.webp`}
              src={`/assets/tracks-backgrounds/${hero.image}-video.mp4`}
            />
          ) : null}
        </div>
      ) : (
        <div className="loading-indicator__background">
          <img alt="" src="/assets/marbles-racetrack-loading-screen.webp" />
        </div>
      )}
    </div>
  )
}
